import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import { CLIENT_TESTIMONIALS } from '../constants/trustData';

/**
 * Shared client-proof section.
 *
 * Renders attributable testimonials from `CLIENT_TESTIMONIALS` (src/constants/trustData.js)
 * when real ones exist. Until then it shows an honest, non-fabricated prompt that points to
 * the anonymized case studies — instead of the previous per-page components that hardcoded
 * fake named testimonials with stock-photo avatars and invented metrics.
 *
 * Accepts (and ignores) any props such as `selectedModel` so it is a drop-in replacement for
 * the earlier page-local components.
 */
const ClientTestimonials = () => {
  const testimonials = CLIENT_TESTIMONIALS;

  if (!testimonials || testimonials.length === 0) {
    return (
      <section className="py-16 lg:py-20 bg-background-alt">
        <div className="container-executive">
          <div className="max-w-3xl mx-auto text-center space-y-6 bg-card border border-border rounded-2xl p-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto">
              <Icon name="ShieldCheck" size={24} color="var(--color-primary)" />
            </div>
            <h2 className="font-headline font-headline-bold text-2xl lg:text-3xl text-text-primary">
              Client Outcomes, Under NDA
            </h2>
            <p className="text-text-secondary">
              We partner with Fortune 500 and global enterprises under confidentiality. Rather than
              publish unverifiable quotes, we share detailed, anonymized transformations and the
              measurable results behind them.
            </p>
            <Button variant="default" size="default" iconName="ArrowRight" iconPosition="right" asChild>
              <Link to="/case-studies">View Case Studies</Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-20 bg-background-alt">
      <div className="container-executive">
        <div className="text-center mb-12">
          <h2 className="font-headline font-headline-bold text-2xl lg:text-3xl text-text-primary">
            What Our Clients Say
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <figure key={t.id || i} className="bg-card border border-border rounded-xl p-6 space-y-4">
              <blockquote className="text-text-secondary leading-relaxed">“{t.quote}”</blockquote>
              <figcaption className="text-sm">
                <span className="font-body-medium text-text-primary">{t.client || t.author}</span>
                {(t.position || t.company) && (
                  <span className="block text-text-secondary">
                    {[t.position, t.company].filter(Boolean).join(', ')}
                  </span>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientTestimonials;
